import { useEffect, useLayoutEffect, useRef, type RefObject } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { isStaffScrollLocked } from './staff-scroll-lock';

const ROUTE_SCREEN_FALLBACK_SELECTOR = '[data-testid="staff-route-screen-fallback"]';
const FOCUS_GUARD_SELECTOR = 'dialog[open], [role="dialog"], [role="alertdialog"], [aria-modal="true"]';
const SETTLE_TIMEOUT_MS = 15_000;

type RouteScrollPosition = {
  main: number;
  viewport: number;
};

const routeScrollPositions = new Map<string, RouteScrollPosition>();

function readViewportScrollTop(): number {
  return document.scrollingElement?.scrollTop ?? window.scrollY ?? 0;
}

function readScrollPosition(main: HTMLElement): RouteScrollPosition {
  return { main: main.scrollTop, viewport: readViewportScrollTop() };
}

function applyScrollPosition(main: HTMLElement, position: RouteScrollPosition): void {
  main.scrollTop = position.main;
  const scrollingElement = document.scrollingElement;
  if (scrollingElement) {
    scrollingElement.scrollTop = position.viewport;
  }
}

function isFocusInsideModal(): boolean {
  const active = document.activeElement;
  if (!(active instanceof Element)) return false;
  return active.closest(FOCUS_GUARD_SELECTOR) != null;
}

function focusRouteContent(main: HTMLElement): HTMLElement | null {
  if (isFocusInsideModal()) return null;
  const heading = main.querySelector<HTMLElement>('h1');
  const target = heading ?? main;
  if (target === heading && !heading.hasAttribute('tabindex')) {
    heading.setAttribute('tabindex', '-1');
  }
  target.focus({ preventScroll: true });
  return target;
}

type PendingRouteFocus = {
  routeKey: string;
  isPop: boolean;
  shouldFocus: boolean;
  targetScroll: RouteScrollPosition | null;
};

export function useStaffRouteTransitions(mainRef: RefObject<HTMLElement | null>): void {
  const location = useLocation();
  const navigationType = useNavigationType();
  const previousLocationRef = useRef<{ key: string } | null>(null);
  const lastScrollPositionRef = useRef<RouteScrollPosition>({ main: 0, viewport: 0 });
  const pendingRef = useRef<PendingRouteFocus | null>(null);
  const autoFocusedElementRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const main = mainRef.current;
    if (!main) return;

    lastScrollPositionRef.current = readScrollPosition(main);
    const saveScrollPosition = () => {
      // Пока drawer/оверлей держит scroll lock, документ может временно отдавать 0:
      // не затираем последнюю стабильную позицию, иначе POP восстановит 0.
      if (isStaffScrollLocked()) return;
      lastScrollPositionRef.current = readScrollPosition(main);
    };
    window.addEventListener('scroll', saveScrollPosition, { passive: true, capture: true });
    return () => {
      window.removeEventListener('scroll', saveScrollPosition, { capture: true });
    };
  }, [mainRef]);

  useEffect(() => {
    if (typeof history === 'undefined' || !('scrollRestoration' in history)) return;
    const previous = history.scrollRestoration;
    history.scrollRestoration = 'manual';
    return () => {
      history.scrollRestoration = previous;
    };
  }, []);

  useLayoutEffect(() => {
    const main = mainRef.current;
    if (!main) return;

    const previous = previousLocationRef.current;
    const isInitial = previous == null;
    if (previous && previous.key !== location.key) {
      routeScrollPositions.set(previous.key, lastScrollPositionRef.current);
    }
    previousLocationRef.current = { key: location.key };

    if (isInitial) {
      lastScrollPositionRef.current = readScrollPosition(main);
      pendingRef.current = null;
      return;
    }

    const isPop = navigationType === 'POP';
    const shouldFocus = navigationType === 'PUSH' || navigationType === 'REPLACE';
    const autoFocusedElement = autoFocusedElementRef.current;
    if (isPop && autoFocusedElement != null && autoFocusedElement === document.activeElement) {
      autoFocusedElement.blur();
    }
    autoFocusedElementRef.current = null;

    const targetScroll = isPop ? routeScrollPositions.get(location.key) ?? null : { main: 0, viewport: 0 };
    applyScrollPosition(main, targetScroll ?? readScrollPosition(main));
    lastScrollPositionRef.current = targetScroll ?? readScrollPosition(main);

    pendingRef.current = { routeKey: location.key, isPop, shouldFocus, targetScroll };
    let cancelled = false;
    let observer: MutationObserver | null = null;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const stopWatching = () => {
      observer?.disconnect();
      observer = null;
      if (timeoutId != null) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    const settle = (): void => {
      const pending = pendingRef.current;
      if (cancelled || !pending || pending.routeKey !== location.key) return;
      if (main.querySelector(ROUTE_SCREEN_FALLBACK_SELECTOR)) return;

      stopWatching();
      if (pending.isPop) {
        if (pending.targetScroll) {
          applyScrollPosition(main, pending.targetScroll);
          lastScrollPositionRef.current = pending.targetScroll;
        }
        return;
      }
      if (pending.shouldFocus) {
        autoFocusedElementRef.current = focusRouteContent(main);
      }
    };

    if (!main.querySelector(ROUTE_SCREEN_FALLBACK_SELECTOR)) {
      settle();
      return;
    }

    observer = new MutationObserver(settle);
    observer.observe(main, { childList: true, subtree: true });
    timeoutId = setTimeout(stopWatching, SETTLE_TIMEOUT_MS);

    return () => {
      cancelled = true;
      stopWatching();
    };
  }, [location.key, navigationType, mainRef]);
}
